import type {
  ChamferFeatureDefinition,
  FaceSetSelection,
  FilletFeatureDefinition,
  FilletType,
  PartialEdgeParam,
  SelectionItemRef,
} from '@puppycad/cad-core';

export type FilletFaceSetId = 'faceSet1' | 'faceSet2';
export type FilletSelectionTarget = 'faces' | 'help-point' | 'partial-edge-reference';
export type ChamferSelectionTarget = 'faces' | 'help-point' | 'partial-edge-reference';

const FACE_FILLET_SET_IDS: FilletFaceSetId[] = ['faceSet1', 'faceSet2'];

export function resolveFilletFaceSetIds(type: FilletType): FilletFaceSetId[] {
  return type === 'face' ? FACE_FILLET_SET_IDS : [];
}

export function resolveDefaultFilletFaceSetId(type: FilletType): FilletFaceSetId | null {
  return resolveFilletFaceSetIds(type)[0] ?? null;
}

export function flattenFaceSetItems(faceSets: FaceSetSelection[] | undefined): SelectionItemRef[] {
  return (faceSets ?? []).flatMap((faceSet) => faceSet.items ?? []);
}

export function upsertFaceSetSelection(
  faceSets: FaceSetSelection[] | undefined,
  setId: FilletFaceSetId,
  item: SelectionItemRef,
): FaceSetSelection[] {
  const nextFaceSets = (faceSets ?? []).map((faceSet) => ({
    ...faceSet,
    items: (faceSet.items ?? []).filter((candidate) => candidate.persistentRef !== item.persistentRef),
  }));
  const existingIndex = nextFaceSets.findIndex((faceSet) => faceSet.setId === setId);
  if (existingIndex >= 0) {
    const current = nextFaceSets[existingIndex]!;
    nextFaceSets[existingIndex] = {
      ...current,
      items: [...(current.items ?? []), item],
    };
    return nextFaceSets.filter((faceSet) => (faceSet.items?.length ?? 0) > 0);
  }
  return [
    ...nextFaceSets.filter((faceSet) => (faceSet.items?.length ?? 0) > 0),
    { setId, items: [item] },
  ];
}

export function removeFaceSetSelection(
  faceSets: FaceSetSelection[] | undefined,
  persistentRef: string,
): FaceSetSelection[] {
  return (faceSets ?? [])
    .map((faceSet) => ({
      ...faceSet,
      items: (faceSet.items ?? []).filter((item) => item.persistentRef !== persistentRef),
    }))
    .filter((faceSet) => (faceSet.items?.length ?? 0) > 0);
}

export function createDefaultPartialEdgeParam(itemRef: string): PartialEdgeParam {
  return {
    itemRef,
    startCondition: 'none',
    endCondition: 'none',
  };
}

export function ensureChamferPartialEdges(
  definition: ChamferFeatureDefinition,
  itemRefs: string[],
): PartialEdgeParam[] {
  const current = definition.partialEdges ?? [];
  const next = itemRefs.map((itemRef) => current.find((item) => item.itemRef === itemRef) ?? createDefaultPartialEdgeParam(itemRef));
  return next;
}

export function resolveFaceSetItemRefs(
  definition: FilletFeatureDefinition,
  setId: FilletFaceSetId,
): string[] {
  return definition.faceSets
    ?.find((faceSet) => faceSet.setId === setId)
    ?.items
    ?.map((item) => item.persistentRef)
    ?? [];
}
